import { Alert, Card, Progress, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDecimal, formatInt } from 'scenes/VisitHistory/utils/format';

import {
  TiendaPerfectaStoreVisitsCompetitionIndicator,
  TiendaPerfectaStoreVisitsInsights,
} from 'models/TiendaPerfecta';

const I18N_BASE = 'scenes.store_visits.competition';
const TOP_BRANDS = 3;

interface StoreVisitsCompetitionProps {
  insights: TiendaPerfectaStoreVisitsInsights | null;
  loading: boolean;
  failed: boolean;
}

/** Dos indicadores de la encuesta POP sobre los locales ejecutados. */
function StoreVisitsCompetition({
  insights,
  loading,
  failed,
}: StoreVisitsCompetitionProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const brandsOf = (indicator: TiendaPerfectaStoreVisitsCompetitionIndicator) =>
    indicator.brands.slice(0, TOP_BRANDS).map((b) => b.name);

  const indicator = (
    key: 'sells_other' | 'visible_competitor',
    data: TiendaPerfectaStoreVisitsCompetitionIndicator
  ) => {
    const brands = brandsOf(data);
    return (
      <div className="store-visits-competition__indicator">
        <div className="store-visits-competition__label">
          {t(`${I18N_BASE}.${key}.label`)}
        </div>
        <div className="store-visits-competition__value">
          {`${formatDecimal(data.pct, language)}%`}
        </div>
        <div className="store-visits-competition__hint">
          {t(`${I18N_BASE}.${key}.${brands.length ? 'hint' : 'hint_empty'}`, {
            count: data.count,
            stores: formatInt(data.count, language),
            brands: brands.join(', '),
            first: brands[0] ?? '',
          })}
        </div>
      </div>
    );
  };

  const progress = insights?.progress;
  const partial = !!progress && !progress.complete;

  return (
    <Card
      className="store-visits-competition"
      title={
        <>
          {t(`${I18N_BASE}.title`)}
          {insights && (
            <span className="visit-history-inc__sub">
              {t(`${I18N_BASE}.subtitle`, {
                stores: formatInt(insights.competition.base, language),
              })}
            </span>
          )}
        </>
      }
    >
      {failed ? (
        <div className="visit-history-inc__empty">
          {t(`${I18N_BASE}.failed`)}
        </div>
      ) : loading || !insights ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <>
          {partial && (
            <Alert
              className="store-visits-competition__progress"
              type="info"
              showIcon
              message={t(`${I18N_BASE}.calculating`, {
                ready: formatInt(progress.ready, language),
                total: formatInt(progress.total, language),
              })}
              description={
                <Progress
                  percent={Math.floor(
                    (progress.ready / Math.max(1, progress.total)) * 100
                  )}
                  size="small"
                />
              }
            />
          )}
          <div className="store-visits-competition__grid">
            {indicator('sells_other', insights.competition.sellsOther)}
            {indicator(
              'visible_competitor',
              insights.competition.visibleCompetitor
            )}
          </div>
        </>
      )}
    </Card>
  );
}

export default StoreVisitsCompetition;
